import type { ChartType, RangeKey } from "@chartandgo/shared";

export const WORKSPACE_VERSION = 2;
const STORAGE_KEY = "chartandgo:workspace:v1";
export const DEFAULT_BASKET_ID = "basket-default";

export type Theme = "light" | "dark";

export interface ChartCard {
  id: string;
  symbol: string;
  name: string;
  range: RangeKey;
  chartType: ChartType;
  showVolume: boolean;
}

export interface GridItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export type NewGridItem = Omit<GridItem, "i">;
export type GridSize = Pick<GridItem, "w" | "h">;
export type CardPlacement = NewGridItem | GridSize;

export interface WorkspaceState {
  version: number;
  theme: Theme;
  activeBasketId: string;
  baskets: StockBasket[];
}

export interface StockBasket {
  id: string;
  name: string;
  cards: ChartCard[];
  layout: GridItem[];
}

export const GRID_COLS = 12;
export const MIN_CARD_W = 3;
export const MIN_CARD_H = 5;
export const DEFAULT_CARD_W = 6;
export const DEFAULT_CARD_H = 8;

export function createDefaultState(): WorkspaceState {
  return {
    version: WORKSPACE_VERSION,
    theme: prefersDark() ? "dark" : "light",
    activeBasketId: DEFAULT_BASKET_ID,
    baskets: [createStockBasket(DEFAULT_BASKET_ID, "My Basket")],
  };
}

export function createStockBasket(id: string, name: string): StockBasket {
  return {
    id,
    name: normalizeBasketName(name),
    cards: [],
    layout: [],
  };
}

function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export type WorkspaceAction =
  | { type: "addCard"; card: ChartCard; layout?: CardPlacement }
  | { type: "removeCard"; id: string }
  | { type: "updateCard"; id: string; changes: Partial<Omit<ChartCard, "id">> }
  | { type: "setLayout"; layout: GridItem[] }
  | { type: "createBasket"; basket: StockBasket }
  | { type: "selectBasket"; id: string }
  | { type: "renameBasket"; id: string; name: string }
  | { type: "removeBasket"; id: string }
  | { type: "setTheme"; theme: Theme }
  | { type: "reset" };

export function workspaceReducer(
  state: WorkspaceState,
  action: WorkspaceAction,
): WorkspaceState {
  switch (action.type) {
    case "addCard": {
      return updateActiveBasket(state, (basket) => {
        const layoutItem = nextLayoutItem(
          basket.layout,
          action.card.id,
          action.layout,
        );
        return {
          ...basket,
          cards: [...basket.cards, action.card],
          layout: [...basket.layout, layoutItem],
        };
      });
    }
    case "removeCard":
      return updateActiveBasket(state, (basket) => ({
        ...basket,
        cards: basket.cards.filter((card) => card.id !== action.id),
        layout: basket.layout.filter((item) => item.i !== action.id),
      }));
    case "updateCard":
      return updateActiveBasket(state, (basket) => ({
        ...basket,
        cards: basket.cards.map((card) =>
          card.id === action.id ? { ...card, ...action.changes } : card,
        ),
      }));
    case "setLayout":
      return updateActiveBasket(state, (basket) => ({
        ...basket,
        layout: mergeLayout(basket, action.layout),
      }));
    case "createBasket":
      if (state.baskets.some((basket) => basket.id === action.basket.id)) {
        return state;
      }
      return {
        ...state,
        activeBasketId: action.basket.id,
        baskets: [...state.baskets, action.basket],
      };
    case "selectBasket":
      return state.baskets.some((basket) => basket.id === action.id)
        ? { ...state, activeBasketId: action.id }
        : state;
    case "renameBasket": {
      const name = normalizeBasketName(action.name);
      return {
        ...state,
        baskets: state.baskets.map((basket) =>
          basket.id === action.id ? { ...basket, name } : basket,
        ),
      };
    }
    case "removeBasket": {
      if (state.baskets.length <= 1) return state;
      const removedIndex = state.baskets.findIndex(
        (basket) => basket.id === action.id,
      );
      if (removedIndex < 0) return state;
      const baskets = state.baskets.filter((basket) => basket.id !== action.id);
      const activeBasketId =
        state.activeBasketId === action.id
          ? baskets[Math.min(removedIndex, baskets.length - 1)]!.id
          : state.activeBasketId;
      return {
        ...state,
        activeBasketId,
        baskets,
      };
    }
    case "setTheme":
      return { ...state, theme: action.theme };
    case "reset":
      return createDefaultState();
    default:
      return state;
  }
}

function updateActiveBasket(
  state: WorkspaceState,
  update: (basket: StockBasket) => StockBasket,
): WorkspaceState {
  return {
    ...state,
    baskets: state.baskets.map((basket) =>
      basket.id === state.activeBasketId ? update(basket) : basket,
    ),
  };
}

/** Place a card at the requested rectangle, or in the first open default slot. */
export function nextLayoutItem(
  layout: GridItem[],
  id: string,
  requested?: CardPlacement,
): GridItem {
  if (requested && "x" in requested) {
    const candidate = normalizeLayoutItem(requested);
    if (!layout.some((item) => rectanglesOverlap(candidate, item))) {
      return { i: id, ...candidate };
    }
  }

  const size = normalizeGridSize(requested ?? {
    w: DEFAULT_CARD_W,
    h: DEFAULT_CARD_H,
  });
  for (let y = 0; ; y += 1) {
    for (let x = 0; x <= GRID_COLS - size.w; x += 1) {
      const candidate: NewGridItem = {
        x,
        y,
        w: size.w,
        h: size.h,
      };
      if (!layout.some((item) => rectanglesOverlap(candidate, item))) {
        return { i: id, ...candidate };
      }
    }
  }
}

function normalizeLayoutItem(item: NewGridItem): NewGridItem {
  const { w, h } = normalizeGridSize(item);
  return {
    x: Math.min(GRID_COLS - w, Math.max(0, Math.round(item.x))),
    y: Math.max(0, Math.round(item.y)),
    w,
    h,
  };
}

function normalizeGridSize(item: GridSize): GridSize {
  return {
    w: Math.min(GRID_COLS, Math.max(MIN_CARD_W, Math.round(item.w))),
    h: Math.max(MIN_CARD_H, Math.round(item.h)),
  };
}

export function rectanglesOverlap(
  a: NewGridItem,
  b: NewGridItem,
): boolean {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

/** Keep only layout entries that correspond to existing cards. */
function mergeLayout(basket: StockBasket, layout: GridItem[]): GridItem[] {
  const cardIds = new Set(basket.cards.map((card) => card.id));
  return layout
    .filter((item) => cardIds.has(item.i))
    .map((item) => ({ i: item.i, x: item.x, y: item.y, w: item.w, h: item.h }));
}

/** Load and validate persisted state, falling back to defaults on any problem. */
export function loadWorkspace(storage: Pick<Storage, "getItem">): WorkspaceState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return createDefaultState();
    const parsed = JSON.parse(raw) as unknown;
    return migrate(parsed);
  } catch {
    return createDefaultState();
  }
}

export function saveWorkspace(
  storage: Pick<Storage, "setItem">,
  state: WorkspaceState,
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be full or unavailable (private mode); persistence is
    // best-effort, so we intentionally swallow the error.
  }
}

/** Coerce unknown persisted data into a valid WorkspaceState. */
export function migrate(data: unknown): WorkspaceState {
  const base = createDefaultState();
  if (!isRecord(data)) return base;

  const theme: Theme = data.theme === "light" ? "light" : "dark";

  // Version 1 stored one flat card grid. Preserve it as the first basket.
  if (!Array.isArray(data.baskets)) {
    const basket = hydrateBasket({
      id: DEFAULT_BASKET_ID,
      name: "My Basket",
      cards: data.cards,
      layout: data.layout,
    });
    return {
      version: WORKSPACE_VERSION,
      theme,
      activeBasketId: basket.id,
      baskets: [basket],
    };
  }

  const seenIds = new Set<string>();
  const baskets = data.baskets
    .filter(isRecord)
    .map((value, index) =>
      hydrateBasket({
        id:
          typeof value.id === "string" && value.id.trim()
            ? value.id
            : `basket-${index + 1}`,
        name:
          typeof value.name === "string" ? value.name : `Basket ${index + 1}`,
        cards: value.cards,
        layout: value.layout,
      }),
    )
    .filter((basket) => {
      if (seenIds.has(basket.id)) return false;
      seenIds.add(basket.id);
      return true;
    });

  if (baskets.length === 0) return { ...base, theme };

  const activeBasketId =
    typeof data.activeBasketId === "string" &&
    baskets.some((basket) => basket.id === data.activeBasketId)
      ? data.activeBasketId
      : baskets[0]!.id;

  return {
    version: WORKSPACE_VERSION,
    theme,
    activeBasketId,
    baskets,
  };
}

function hydrateBasket(value: {
  id: string;
  name: string;
  cards: unknown;
  layout: unknown;
}): StockBasket {
  const cards = Array.isArray(value.cards)
    ? value.cards.filter(isValidCard)
    : [];
  const cardIds = new Set(cards.map((card) => card.id));
  const layout = Array.isArray(value.layout)
    ? value.layout.filter(isValidLayout).filter((item) => cardIds.has(item.i))
    : [];

  const layoutIds = new Set(layout.map((item) => item.i));
  for (const card of cards) {
    if (!layoutIds.has(card.id)) {
      layout.push(nextLayoutItem(layout, card.id));
    }
  }

  return {
    id: value.id,
    name: normalizeBasketName(value.name),
    cards,
    layout,
  };
}

function normalizeBasketName(name: string): string {
  const trimmed = name.trim().slice(0, 40);
  return trimmed || "Untitled Basket";
}

const VALID_RANGES: RangeKey[] = [
  "1D",
  "5D",
  "1M",
  "3M",
  "6M",
  "YTD",
  "1Y",
  "5Y",
  "MAX",
];
const VALID_TYPES: ChartType[] = ["candlestick", "line", "area", "bar"];

function isValidCard(value: unknown): value is ChartCard {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.symbol === "string" &&
    typeof value.name === "string" &&
    typeof value.showVolume === "boolean" &&
    VALID_RANGES.includes(value.range as RangeKey) &&
    VALID_TYPES.includes(value.chartType as ChartType)
  );
}

function isValidLayout(value: unknown): value is GridItem {
  if (!isRecord(value)) return false;
  return (
    typeof value.i === "string" &&
    typeof value.x === "number" &&
    typeof value.y === "number" &&
    typeof value.w === "number" &&
    typeof value.h === "number"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export const STORAGE_KEY_FOR_TESTS = STORAGE_KEY;
